import { useEffect, useState } from 'react'
import { Moon, Sun } from 'lucide-react'

type Theme = 'light' | 'dark'

/**
 * Reads the effective theme: an explicit choice if one was made, otherwise light.
 *
 * Deliberately NOT following prefers-color-scheme. The product is designed and
 * reviewed light-first, and a visitor whose OS happens to be dark would otherwise
 * land on a palette nobody signed off. Dark stays one click away and is remembered.
 */
function resolve(): Theme {
  const saved = localStorage.getItem('bms-theme')
  if (saved === 'light' || saved === 'dark') return saved
  return 'light'
}

/**
 * Light/dark switch. Persists the explicit choice to localStorage and stamps `data-theme`
 * on <html> — the CSS token layer already defines both palettes, so the switch is instant
 * and every screen follows without per-component work.
 */
export function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>(resolve)

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
    localStorage.setItem('bms-theme', theme)
  }, [theme])

  const next = theme === 'dark' ? 'light' : 'dark'
  return (
    <button className="theme-toggle" type="button" title={`Switch to ${next} mode`}
            aria-label={`Switch to ${next} mode`} onClick={() => setTheme(next)}>
      {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
    </button>
  )
}
